import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxToggleComponent } from "@components/checkbox-side/checkbox-toggle.component";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { HeaderComponent } from "@components/header/header.component";
import { SelectComponent } from "@components/select/select.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AccountType } from "app/interfaces/api/AccountType";
import { EmployeeUpdateRequest } from "app/interfaces/employee.interface";
import { SelectValue } from "app/interfaces/select-value.interface";
import { ApiService, UserInfo } from "app/services/api.service";

@Component({
  selector: 'app-employee-form-page',
  templateUrl: './employee-form-page.component.html',
  styleUrls: ['./employee-form-page.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonComponent, CheckboxToggleComponent, SelectComponent, HeaderComponent, ConfirmModalComponent, MatTooltipModule]
})

export class EmployeeFormPageComponent implements OnInit {
    AccountType = AccountType;
    userInfo: UserInfo;

    firstName: string = '';
    lastName: string = '';
    email: string = '';
    phone: string = '';
    primary: boolean = false;
    active: boolean = false;
    enabledGlobalRewards: boolean = false;
    enabledEcosentino: boolean = false;
    enabledFinancialPortal: boolean = false;
    receiveLeads: boolean = false;
    cnexus: boolean = false;

    position: string = ''
    selectedPosition: SelectValue | null = null
    positions: SelectValue[] = [
        { id: 'my_profile.user_section.position.director', label: this.translate.instant('my_profile.user_section.position.director'), value: 'Director' },
        {
            id: 'my_profile.user_section.position.executive_vice_president',
            label: this.translate.instant('my_profile.user_section.position.executive_vice_president'),
            value: 'Executive Vice President',
        },
        { id: 'my_profile.user_section.position.chief_of_staff', label: this.translate.instant('my_profile.user_section.position.chief_of_staff'), value: 'Chief of Staff' },
        { id: 'my_profile.user_section.position.architect', label: this.translate.instant('my_profile.user_section.position.architect'), value: 'Architect' },
        { id: 'my_profile.user_section.position.design_staff', label: this.translate.instant('my_profile.user_section.position.design_staff'), value: 'Design Staff' },
        { id: 'my_profile.user_section.position.project_leader', label: this.translate.instant('my_profile.user_section.position.project_leader'), value: 'Project Leader' },
        {
            id: 'my_profile.user_section.position.commercial_interior_designer',
            label: this.translate.instant('my_profile.user_section.position.commercial_interior_designer'),
            value: 'Commercial Interior Designer',
        },
        {
            id: 'my_profile.user_section.position.assistant_vice_president',
            label: this.translate.instant('my_profile.user_section.position.assistant_vice_president'),
            value: 'Assistant Vice President',
        },
        {
            id: 'my_profile.user_section.position.marketing_director',
            label: this.translate.instant('my_profile.user_section.position.marketing_director'),
            value: 'Marketing Director',
        },
        {
            id: 'my_profile.user_section.position.construction_manager',
            label: this.translate.instant('my_profile.user_section.position.construction_manager'),
            value: 'Construction Manager',
        },
        {
            id: 'my_profile.user_section.position.director_or_vp_of_purchasing',
            label: this.translate.instant('my_profile.user_section.position.director_or_vp_of_purchasing'),
            value: 'Director or VP of Purchasing',
        },
        {
            id: 'my_profile.user_section.position.sales_representative',
            label: this.translate.instant('my_profile.user_section.position.sales_representative'),
            value: 'Sales Representative',
        },
        {
            id: 'my_profile.user_section.position.customer_service_representative',
            label: this.translate.instant('my_profile.user_section.position.customer_service_representative'),
            value: 'Customer Service Representative',
        },
        { id: 'my_profile.user_section.position.owner', label: this.translate.instant('my_profile.user_section.position.owner'), value: 'Owner' },
    ]

    isPrimaryDisabled: boolean = false;
    selectedPrimary: SelectValue | null = null;
    primaryOptions: SelectValue[] = [
        { id: 'admin', label: 'Admin', value: true },
        { id: 'user', label: 'User', value: false }
    ];

    employee: EmployeeUpdateRequest = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        contactType: '',
        primary: false,
        active: false,
        enabledGlobalRewards: false,
        enabledEcosentino: false,
        enabledFinancialPortal: false,
        receiveLeads: false,
        cnexus: false
    };

    originalEmployee: EmployeeUpdateRequest | null = null;
    isEmailDisabled: boolean = false;
    isEditMode = false;
    hasChanges = false;

    shopId: string | null = null;

    isCreateSuccessModalVisible: boolean = false;
    isEditSuccessModalVisible: boolean = false;

    constructor(private route: ActivatedRoute, private router: Router, private api: ApiService, private translate: TranslateService) {}

    ngOnInit(): void {
        const emp = history.state?.employee;
        this.shopId = history.state?.shopId ?? null;
        if (emp) {
            this.firstName = emp.firstName;
            this.lastName = emp.lastName;
            this.email = emp.email;
            this.phone = emp.phone === 'null' ? '' : emp.phone;
            this.selectedPosition = {id: emp.typeLabel, label: emp.typeLabel ?? emp.contactType, value: emp.contactType };
            this.position = emp.contactType;
            this.primary = emp.primary;
            this.active = emp.active;
            this.enabledGlobalRewards = emp.enabledGlobalRewards;
            this.enabledEcosentino = emp.enabledEcosentino;
            this.enabledFinancialPortal = emp.enabledFinancialPortal;
            this.receiveLeads = emp.receiveLeads;
            this.cnexus = emp.cnexus;

            this.isEditMode = true;
            this.originalEmployee = {
                id: emp.id,
                firstName: emp.firstName ?? '',
                lastName: emp.lastName ?? '',
                email: emp.email ?? '',
                phone: emp.phone === 'null' ? '' : emp.phone ?? '',
                contactType: emp.contactType ?? '',
                primary: emp.primary,
                active: emp.active,
                enabledGlobalRewards: emp.enabledGlobalRewards,
                enabledEcosentino: emp.enabledEcosentino,
                enabledFinancialPortal: emp.enabledFinancialPortal,
                receiveLeads: emp.receiveLeads,
                cnexus: emp.cnexus
            };

            if (emp.email && emp.email.trim() !== '') {
                this.isEmailDisabled = true;
            }

            if (emp.primary !== undefined) {
                this.selectedPrimary = this.primaryOptions.find(p => p.value === emp.primary) || null;
            }

            if (emp.contactType && emp.contactType !== 'null') {
                this.selectedPosition = this.positions.find(p => p.value === emp.contactType) || null;
            } else {
                this.selectedPosition = null;
            }

            this.api.userInfo.pipe().subscribe((userInfo) => {
                this.userInfo = userInfo;
                if (this.userInfo?.role !== 'ADMIN') {
                    this.primary = false;
                    this.selectedPrimary = this.primaryOptions.find(p => p.value === false) || null;
                    this.isPrimaryDisabled = true;
                }
            });

            const employees$ = this.shopId
                ? this.api.getShopEmployees(this.shopId)
                : this.api.getEmployees();

            employees$.subscribe((employees) => {
                const adminCount = employees.filter(e => e.primary).length;
                if (adminCount >= 2 && !emp.primary) {
                    this.primary = false;
                    this.selectedPrimary = this.primaryOptions.find(p => p.value === false) || null;
                    this.isPrimaryDisabled = true;
                }
            });

        } else {
            this.api.userInfo.pipe().subscribe((userInfo) => {
                this.userInfo = userInfo;
                
                const employees$ = this.shopId
                    ? this.api.getShopEmployees(this.shopId)
                    : this.api.getEmployees();

                employees$.subscribe((employees) => {
                    const adminCount = employees.filter(e => e.primary).length;

                    if (this.userInfo?.role !== 'ADMIN' || adminCount >= 2) {
                        this.primary = false;
                        this.selectedPrimary = this.primaryOptions.find(p => p.value === false) || null;
                        this.isPrimaryDisabled = true;
                    }
                });
            });
        }
    }

    getUserPositions() {
        return this.positions;
    }

    selectUserPosition($event: SelectValue | null) {
        this.position = $event?.value;
        this.selectedPosition = $event;
        this.markDirty();
    }

    selectPrimary($event: SelectValue | null) {
        this.primary = $event?.value ?? false;
        this.selectedPrimary = $event;
        this.markDirty();
    }

    markDirty(): void {
        if (!this.isEditMode || !this.originalEmployee) {
            this.hasChanges = false;
            return;
        }

        const changes = {
            firstNameChanged: this.firstName !== this.originalEmployee.firstName,
            lastNameChanged: this.lastName !== this.originalEmployee.lastName,
            emailChanged: this.email !== this.originalEmployee.email,
            phoneChanged: this.phone !== this.originalEmployee.phone,
            positionChanged: this.position !== this.originalEmployee.contactType,
            primaryChanged: this.primary !== this.originalEmployee.primary,
            activeChanged: this.active !== this.originalEmployee.active,
            enabledGlobalRewardsChanged: this.enabledGlobalRewards !== this.originalEmployee.enabledGlobalRewards,
            enabledEcosentinoChanged: this.enabledEcosentino !== this.originalEmployee.enabledEcosentino,
            enabledFinancialPortalChanged: this.enabledFinancialPortal !== this.originalEmployee.enabledFinancialPortal,
            receiveLeadsChanged: this.receiveLeads !== this.originalEmployee.receiveLeads,
            cnexusChanged: this.cnexus !== this.originalEmployee.cnexus
        };

        this.hasChanges = Object.values(changes).some(v => v);
    }


    canSave(): boolean {
        const requiredFilled =
            !!this.firstName.trim() &&
            !!this.lastName.trim() &&
            !!this.email.trim() &&
            !!this.phone.trim();

        if (!this.isEditMode) {
            return requiredFilled;
        }

        return requiredFilled && this.hasChanges;
    }


    onSave() {
        const employee: EmployeeUpdateRequest = {
            id: this.isEditMode ? history.state?.employee?.id : undefined,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            phone: this.phone,
            contactType: this.position,
            primary: this.primary,
            active: this.active,
            enabledGlobalRewards: this.enabledGlobalRewards,
            enabledEcosentino: this.enabledEcosentino,
            enabledFinancialPortal: this.enabledFinancialPortal,
            receiveLeads: this.receiveLeads,
            cnexus: this.cnexus
        };

        const save$ = this.shopId
            ? this.api.saveShopEmployee(this.shopId, employee)
            : this.api.saveEmployee(employee);

        save$.subscribe(() => {
            if (this.isEditMode) {
            this.isEditSuccessModalVisible = true;
            } else {
            this.isCreateSuccessModalVisible = true;
            }
        });
    }
    
    closeSuccessModal() {
        const tab = this.shopId ? 'SHOPS' : 'EMPLOYEES';
        this.isCreateSuccessModalVisible = false;
        this.isEditSuccessModalVisible = false;
        this.router.navigate(['/profile'], { queryParams: { tab } });
    }

    onCancel() {
        this.router.navigate(['/profile'], {
            queryParams: { tab: this.shopId ? 'SHOPS' : 'EMPLOYEES' }
        });
    }
}
